import { useDebounce } from "_metronic/helpers";
import { useEffect, useRef, useState } from "react";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  globalSearch,
  selectFilteredSearchResults,
  selectSearchFilters,
  selectSearching,
  setSelectedSearchFilter,
} from "./components/GlobalSearchSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { buildSearchResultRedirectRoute } from "./components/RecordTypeRouteBuilder";
import { ClientPortalGlobalSearchResultModel } from "services/api/cplapi/CPLAPI";
import "./components/GlobalSearchInput.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/pro-light-svg-icons";
import { ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { useMediaLayout } from "cpl-react-common/Hooks/useMediaQuery";
import { useOutsideClick } from "hooks/useOutsideClick";

const GlobalSearchInput: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const thunkDispatch = useThunkDispatch();
  const dispatch = useDispatch();

  const { isMobile } = useMediaLayout();

  const searchResults = useSelector(selectFilteredSearchResults);
  const searchFilters = useSelector(selectSearchFilters);
  const searching = useSelector(selectSearching);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [hideSuggestions, setHideSuggestions] = useState(true);

  const quickSelectSearchResults = searchResults.slice(0, 10);
  const showMore = searchResults.length > 10;
  const debouncedSearchTerm = useDebounce(searchTerm, 800);
  const noResults = debouncedSearchTerm !== "" && searchResults.length === 0;
  const hasSuggestionFeedback = quickSelectSearchResults.length > 0 || noResults;
  const isOnSearchResultsTablePage = location.pathname === "/SearchResults";
  const shouldHideSuggestions = hideSuggestions || isOnSearchResultsTablePage || !hasSuggestionFeedback;

  useEffect(() => {
    thunkDispatch(globalSearch({ searchTerm: debouncedSearchTerm! }));
  }, [debouncedSearchTerm, thunkDispatch]);

  function redirectToResult(searchResult: ClientPortalGlobalSearchResultModel) {
    const path = buildSearchResultRedirectRoute(searchResult);
    navigate(path);
  }

  function showMoreResults() {
    setHideSuggestions(true);
    navigate("/SearchResults");
  }

  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, () => setHideSuggestions(true));

  return (
    <div className="rounded-5 ps-2 pe-2 pt-1 pb-1" ref={ref}>
      <div className="input-group global-search-input">
        <input
          id="global-search-box"
          className="form-control"
          type="text"
          placeholder="Search..."
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setHideSuggestions(false)}
          aria-label="Global search"
          data-test-id="global-search__search-input"
        />
        <div className="input-group-append global-search-search-button align-content-center ms-2">
          <ActivityIndicator isBusy={searching}>
            <div
              className="cursor-pointer symbol"
              onClick={() => thunkDispatch(globalSearch({ searchTerm: debouncedSearchTerm! }))}
              aria-valuetext="Global Search"
              data-test-id="global-search__search-button"
            >
              <FontAwesomeIcon icon={faSearch} size="xl" aria-hidden="true" className="toolbar-icon" />
              <div aria-label="Search button" hidden>
                Search
              </div>
            </div>
          </ActivityIndicator>
        </div>
      </div>
      <div
        className={`${"suggestions shadow-sm"} ${shouldHideSuggestions ? "hidden" : ""} ${
          isMobile ? "suggestions-mobile" : ""
        }`}
      >
        {quickSelectSearchResults.length > 0 && (
          <ToggleButtonGroup
            type="radio"
            name={"global-search-box-filter-selector"}
            onChange={(searchFilter) => dispatch(setSelectedSearchFilter(searchFilter))}
            defaultValue={searchFilters.find((i) => i.isDefault)?.value ?? searchFilters[0].value}
            className="m-2"
          >
            {searchFilters.map((searchFilter) => (
              <ToggleButton
                key={"global-search-box-filter-" + searchFilter.value}
                id={"global-search-box-filter-" + searchFilter.value}
                variant={"outline-secondary"}
                value={searchFilter.value}
                className="rounded-5 ps-2 pe-2 pt-1 pb-1"
                data-test-id={`global-search__filter-radio-${searchFilter.value}`}
              >
                {searchFilter.displayName}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        {quickSelectSearchResults.map((suggestion) => (
          <div
            key={suggestion.type! + suggestion.recordID}
            className={"suggestion border-bottom px-2 py-3"}
            onClick={() => redirectToResult(suggestion)}
            data-test-id={`global-search__result-link-${suggestion.type}-${suggestion.recordID}`}
          >
            <span className="badge badge-primary me-2">{suggestion.label}</span>
            <span className="suggestion-record-text">{suggestion.text}</span>
          </div>
        ))}
        {showMore && (
          <div
            className={"suggestion px-2 py-2 w-100 d-inline-block text-center"}
            onClick={showMoreResults}
            data-test-id="global-search__show-more-link"
          >
            Show More...
          </div>
        )}
        {noResults && !searching && (
          <div className={"suggestion px-2 py-2 w-100 d-inline-block text-center"}>No results found</div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearchInput;
